import React, { useState, useEffect, useRef } from 'react';
import { AppState, Chapter, Character, WorldItem, MuseMessage, ThemeMode, CritiqueItem, BookProject, MuseContextType, MuseAction } from '../types';
import * as GeminiService from '../services/geminiService';
import { Editor } from './Editor';
import { Icons } from './Icons';
import { NotificationToast } from './NotificationToast';

// Helper type for the props expected by Dashboard, splitting AppState slightly
type DashboardProps = {
  state: {
      project: BookProject;
      themeMode: ThemeMode;
      activeTab: string;
      activeChapterId: string | null;
  };
  onStateChange: (updates: { 
      project?: BookProject; 
      viewMode?: 'projects'; 
      activeChapterId?: string | null;
      activeTab?: any;
      themeMode?: ThemeMode;
  }) => void;
  onLogout: () => void;
};

export const Dashboard = ({ 
  state, 
  onStateChange, 
  onLogout 
}: DashboardProps) => {
  const { project, activeTab, activeChapterId } = state;
  const [isWriting, setIsWriting] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date>(new Date());
  const [notification, setNotification] = useState<{message: string, type: 'success'|'error'} | null>(null);
  
  // Theme State
  useEffect(() => {
    const body = document.body;
    body.classList.remove('theme-light', 'theme-dark', 'theme-sepia', 'bg-gray-50', 'bg-gray-900', 'bg-amber-50');
    
    if (state.themeMode === 'dark') {
      body.classList.add('bg-gray-900', 'text-gray-100', 'theme-dark');
    } else if (state.themeMode === 'sepia') {
      body.classList.add('bg-[#f4e4bc]', 'text-gray-900', 'theme-sepia');
    } else {
      body.classList.add('bg-gray-50', 'text-gray-900', 'theme-light');
    }
  }, [state.themeMode]);

  // View States
  const [viewModeOutline, setViewModeOutline] = useState<'list' | 'grid'>('list');
  const [showMuse, setShowMuse] = useState(false);
  const [showFindReplace, setShowFindReplace] = useState(false);
  
  // New Feature States
  const [isZenMode, setIsZenMode] = useState(false);
  const [showCritique, setShowCritique] = useState(false);
  const [critiqueLoading, setCritiqueLoading] = useState(false);
  const [critiqueData, setCritiqueData] = useState<CritiqueItem[]>([]);
  
  // Editing States
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);
  const [tempChapterData, setTempChapterData] = useState<{title: string, summary: string} | null>(null);
  
  const [editingCharId, setEditingCharId] = useState<string | null>(null);
  const [tempCharData, setTempCharData] = useState<Character | null>(null);

  const [editingWorldId, setEditingWorldId] = useState<string | null>(null);
  const [tempWorldData, setTempWorldData] = useState<WorldItem | null>(null);

  // Chat State
  const [museInput, setMuseInput] = useState('');
  const [isMuseThinking, setIsMuseThinking] = useState(false);
  const [museContext, setMuseContext] = useState<MuseContextType | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Find & Replace State
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [replaceResult, setReplaceResult] = useState<string | null>(null);

  // TTS State
  const [isSpeaking, setIsSpeaking] = useState(false);
  const synth = window.speechSynthesis;

  if (!project) return null;

  const activeChapter = project.outline.find(c => c.id === activeChapterId);
  const isBookComplete = project.outline.every(c => c.isDrafted && c.content.length > 100);

  // --- Auto-Save Logic (Actually handled by App.tsx, but we update timestamp here) ---
  useEffect(() => {
    const interval = setInterval(() => {
      setLastSaved(new Date());
    }, 60000); 

    return () => clearInterval(interval);
  }, []);

  // Scroll chat to bottom
  useEffect(() => {
    if (showMuse && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [project.museHistory, showMuse, isMuseThinking, museContext]);

  // --- Logic: Chapter Locking ---
  const isChapterLocked = (index: number) => {
    if (index === 0) return false;
    return !project.outline[index - 1].isDrafted;
  };

  // --- Handlers: Muse Agent ---
  
  const startMuseSession = (context: MuseContextType) => {
    setMuseContext(context);
  };

  const handleNewChat = () => {
    if(window.confirm("Start a new chat session? This will clear the current conversation.")) {
        onStateChange({ project: { ...project, museHistory: [] } });
        setMuseContext(null);
    }
  };

  const handleMuseSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!museInput.trim()) return;

    // Default to story context if not set (shouldn't happen with UI, but safety first)
    const currentContext = museContext || 'story';

    const userMsg: MuseMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      text: museInput,
      timestamp: Date.now()
    };

    const history = project.museHistory || [];
    const newHistory = [...history, userMsg];
    
    onStateChange({ project: { ...project, museHistory: newHistory } });
    setMuseInput('');
    setIsMuseThinking(true);

    try {
      const response = await GeminiService.askMuse(
        project, 
        newHistory, 
        userMsg.text, 
        currentContext, 
        activeChapterId
      );

      const aiMsg: MuseMessage = {
        id: crypto.randomUUID(),
        role: 'model',
        text: response.text,
        timestamp: Date.now(),
        actionProposal: response.actionProposal // Attach structured action if present
      };
      
      onStateChange({ project: { ...project, museHistory: [...newHistory, aiMsg] } });
    } catch (err) {
      console.error(err);
      setNotification({ message: "Muse failed to respond.", type: 'error' });
    } finally {
      setIsMuseThinking(false);
    }
  };

  const handleApplyAction = (action: MuseAction, messageId: string) => {
     if (action.type === 'create_character') {
        const newChar: Character = {
            id: crypto.randomUUID(),
            name: action.data.name || "New Character",
            role: action.data.role || "Supporting",
            description: action.data.description || "",
            arc: action.data.arc || ""
        };
        onStateChange({ project: { ...project, characters: [...project.characters, newChar] } });
        setNotification({ message: `Added character: ${newChar.name}`, type: 'success' });
     } 
     else if (action.type === 'rewrite_chapter') {
        if (!activeChapter) {
            setNotification({ message: "No active chapter to rewrite.", type: 'error' });
            return;
        }
        const updatedOutline = project.outline.map(c => 
            c.id === activeChapter.id 
              ? { ...c, content: action.data.content } 
              : c
        );
        onStateChange({ project: { ...project, outline: updatedOutline } });
        setNotification({ message: "Chapter content updated.", type: 'success' });
     }
  };

  // --- Handlers: Red Pen Critique ---
  const handleCritique = async () => {
    if (!activeChapter || !activeChapter.content) return;
    
    if (showCritique && critiqueData.length > 0) {
      setShowCritique(false);
      return;
    }

    setShowCritique(true);
    setCritiqueLoading(true);
    setCritiqueData([]);

    try {
      const results = await GeminiService.critiqueChapter(project, activeChapter);
      setCritiqueData(results);
    } catch (e: any) {
      console.error(e);
      alert("Critique failed: " + e.message);
      setShowCritique(false);
    } finally {
      setCritiqueLoading(false);
    }
  };

  // --- Handlers: Find & Replace ---
  const handleGlobalReplace = () => {
    if (!findText) return;
    let count = 0;
    const newOutline = project.outline.map(ch => {
      if (!ch.content) return ch;
      const regex = new RegExp(findText, 'g');
      const matches = ch.content.match(regex);
      if (matches) {
        count += matches.length;
        return { ...ch, content: ch.content.replace(regex, replaceText) };
      }
      return ch;
    });

    if (count > 0) {
      onStateChange({ project: { ...project, outline: newOutline } });
      setReplaceResult(`Replaced ${count} occurrences.`);
    } else {
      setReplaceResult("No matches found.");
    }
    setTimeout(() => setReplaceResult(null), 3000);
  };

  // --- Handlers: TTS ---
  const toggleTTS = () => {
    if (isSpeaking) {
      synth.cancel();
      setIsSpeaking(false);
    } else {
      if (!activeChapter?.content) return;
      const textToRead = activeChapter.content.replace(/<[^>]*>?/gm, '');
      const utterance = new SpeechSynthesisUtterance(textToRead);
      utterance.onend = () => setIsSpeaking(false);
      synth.speak(utterance);
      setIsSpeaking(true);
    }
  };

  // --- Handlers: Writing ---
  const handleWriteChapter = async () => {
    if (!activeChapter) return;
    setIsWriting(true);
    try {
      const currentIndex = project.outline.findIndex(c => c.id === activeChapter.id);
      const prevChapter = currentIndex > 0 ? project.outline[currentIndex - 1] : null;
      
      const content = await GeminiService.writeChapter(
        {
          title: project.title,
          style: project.style,
          genre: project.genre,
          characters: project.characters,
          synopsis: project.synopsis,
          language: project.language,
          styleGuide: project.styleGuide,
          worldSetting: project.worldSetting
        },
        activeChapter,
        prevChapter ? prevChapter.summary : null
      );

      const updatedOutline = project.outline.map(c => 
        c.id === activeChapter.id 
          ? { ...c, content: content, isDrafted: true } 
          : c
      );

      onStateChange({
        project: { ...project, outline: updatedOutline }
      });

    } catch (e: any) {
      console.error(e);
      alert("Failed to generate chapter: " + e.message);
    } finally {
      setIsWriting(false);
    }
  };

  const handleContinueWriting = async () => {
    if (!activeChapter || !activeChapter.content) return;
    setIsContinuing(true);
    try {
      const newContent = await GeminiService.continueChapter(
        {
          title: project.title,
          style: project.style,
          genre: project.genre,
          characters: project.characters,
          synopsis: project.synopsis,
          language: project.language,
          styleGuide: project.styleGuide,
          worldSetting: project.worldSetting
        },
        activeChapter,
        activeChapter.content
      );

      const updatedContent = activeChapter.content + "\n" + newContent;
      const updatedOutline = project.outline.map(c => 
        c.id === activeChapter.id 
          ? { ...c, content: updatedContent } 
          : c
      );

      onStateChange({
        project: { ...project, outline: updatedOutline }
      });
    } catch (e: any) {
      console.error(e);
      alert("Failed to continue writing: " + e.message);
    } finally {
      setIsContinuing(false);
    }
  };

  const handleNextChapter = () => {
    if (!activeChapter) return;
    const currentIndex = project.outline.findIndex(c => c.id === activeChapter.id);
    if (currentIndex < project.outline.length - 1) {
      const nextChapter = project.outline[currentIndex + 1];
      onStateChange({ activeChapterId: nextChapter.id, activeTab: 'outline' });
    } else {
      alert("This is the last chapter!");
    }
  };

  const handleStartManual = () => {
    if (!activeChapter) return;
    const updatedOutline = project.outline.map(c => 
      c.id === activeChapter.id 
        ? { ...c, content: ' ', isDrafted: true } 
        : c
    );
    onStateChange({
      project: { ...project, outline: updatedOutline }
    });
  };

  const handleUpdateContent = (newContent: string) => {
    if (!activeChapter) return;
    const updatedOutline = project.outline.map(c => 
      c.id === activeChapter.id 
        ? { ...c, content: newContent } 
        : c
    );
    onStateChange({
      project: { ...project, outline: updatedOutline }
    });
  };

  // --- Handlers: Downloads (Aesthetic) ---
  const generateHTMLTemplate = (title: string, contentBody: string) => {
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <title>${title}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,400;0,600;1,400&family=Cinzel:wght@400;700&display=swap');
        body { font-family: 'Crimson Pro', serif; font-size: 14pt; line-height: 1.6; color: #1a1a1a; max-width: 800px; margin: 0 auto; padding: 4rem 2rem; background-color: #fdfbf7; }
        h1.book-title { font-family: 'Cinzel', serif; font-size: 3.5rem; text-align: center; margin-bottom: 1rem; color: #000; letter-spacing: 0.1em; text-transform: uppercase; }
        h2.author-name { font-family: 'Crimson Pro', serif; font-size: 1.5rem; text-align: center; margin-bottom: 4rem; font-style: italic; color: #555; }
        h3.chapter-title { font-family: 'Cinzel', serif; font-size: 2rem; margin-top: 4rem; margin-bottom: 2rem; text-align: center; border-bottom: 1px solid #ddd; padding-bottom: 1rem; }
        p { margin-bottom: 1rem; text-align: justify; }
        p.drop-cap:first-letter { float: left; font-size: 3.5rem; line-height: 0.8; padding-top: 4px; padding-right: 8px; padding-left: 3px; font-family: 'Cinzel', serif; }
        .page-break { page-break-before: always; }
        .center-mark { text-align: center; margin: 2rem 0; color: #888; }
        .copyright { text-align: center; font-size: 0.9rem; margin-top: 8rem; color: #666; }
    </style>
</head>
<body>${contentBody}</body>
</html>`;
  };

  const downloadFile = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadChapter = () => {
    if (!activeChapter) return;
    const content = `
        <h3 class="chapter-title">${activeChapter.title}</h3>
        ${activeChapter.content.split('\n').map((p, i) => 
            p.trim() ? `<p class="${i===0 ? 'drop-cap' : ''}">${p}</p>` : ''
        ).join('')}
    `;
    const html = generateHTMLTemplate(activeChapter.title, content);
    downloadFile(`${activeChapter.title.replace(/\s+/g, '_')}.html`, html);
  };

  const handleDownloadBook = () => {
    let body = `
        <div style="height: 80vh; display: flex; flex-direction: column; justify-content: center;">
            <h1 class="book-title">${project.title}</h1>
            <h2 class="author-name">A Novel</h2>
        </div>
        <div class="page-break"></div>
        <div class="copyright">
            <p>Generated by BookWiz AI</p>
            <p>${new Date().getFullYear()}</p>
        </div>
        <div class="page-break"></div>
    `;
    project.outline.forEach(chapter => {
        if (chapter.isDrafted) {
            body += `
                <div class="page-break"></div>
                <h3 class="chapter-title">${chapter.title}</h3>
                ${chapter.content.split('\n').map((p, i) => 
                    p.trim() ? `<p class="${i===0 ? 'drop-cap' : ''}">${p}</p>` : ''
                ).join('')}
                <div class="center-mark">***</div>
            `;
        }
    });
    const html = generateHTMLTemplate(project.title, body);
    downloadFile(`${project.title.replace(/\s+/g, '_')}_Full_Manuscript.html`, html);
  };

  // --- Handlers: Editing Outline ---
  const startEditChapter = (e: React.MouseEvent, chapter: Chapter) => {
    e.stopPropagation();
    setEditingChapterId(chapter.id);
    setTempChapterData({ title: chapter.title, summary: chapter.summary });
  };

  const saveChapterEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingChapterId || !tempChapterData) return;
    const updatedOutline = project.outline.map(c => 
      c.id === editingChapterId
        ? { ...c, title: tempChapterData.title, summary: tempChapterData.summary }
        : c
    );
    onStateChange({ project: { ...project, outline: updatedOutline } });
    setEditingChapterId(null);
    setTempChapterData(null);
  };

  // --- Handlers: Editing Characters ---
  const startEditChar = (char: Character) => {
    setEditingCharId(char.id);
    setTempCharData({ ...char });
  };

  const saveCharEdit = () => {
    if (!editingCharId || !tempCharData) return;
    const updatedChars = project.characters.map(c => 
      c.id === editingCharId ? tempCharData : c
    );
    onStateChange({ project: { ...project, characters: updatedChars } });
    setEditingCharId(null);
    setTempCharData(null);
  };

  // --- Handlers: Editing World Items ---
  const addWorldItem = () => {
    const newItem: WorldItem = {
      id: crypto.randomUUID(),
      name: "New Item",
      category: "Location",
      description: "Description..."
    };
    onStateChange({ project: { ...project, worldBible: [...(project.worldBible || []), newItem] } });
    startEditWorld(newItem);
  };

  const startEditWorld = (item: WorldItem) => {
    setEditingWorldId(item.id);
    setTempWorldData({ ...item });
  };

  const saveWorldEdit = () => {
    if (!editingWorldId || !tempWorldData) return;
    const updatedWorld = (project.worldBible || []).map(w => 
      w.id === editingWorldId ? tempWorldData : w
    );
    onStateChange({ project: { ...project, worldBible: updatedWorld } });
    setEditingWorldId(null);
    setTempWorldData(null);
  };
  
  const deleteWorldItem = (id: string) => {
    if(window.confirm("Delete this item?")) {
       onStateChange({ project: { ...project, worldBible: project.worldBible.filter(w => w.id !== id)}});
    }
  }

  // --- RENDERERS ---

  const renderOutlineTab = () => {
    // If we have an active chapter selected in Outline tab, show the editor for it
    // This effectively merges "Write" into "Outline"
    if (activeChapterId) {
      return renderWriteView();
    }

    return (
    <div className="space-y-6 pb-20 animate-fade-in-up">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-serif font-bold text-ink dark:text-gray-100">Chapter Outline</h2>
        <div className="flex gap-2">
            <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg flex text-xs">
                <button 
                  onClick={() => setViewModeOutline('list')}
                  className={`px-3 py-1.5 rounded ${viewModeOutline === 'list' ? 'bg-white dark:bg-gray-600 shadow text-ink dark:text-white' : 'text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                  <Icons.List className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => setViewModeOutline('grid')}
                  className={`px-3 py-1.5 rounded ${viewModeOutline === 'grid' ? 'bg-white dark:bg-gray-600 shadow text-ink dark:text-white' : 'text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                >
                  <Icons.Grid className="w-4 h-4" />
                </button>
            </div>
            {isBookComplete && (
                <button 
                  onClick={handleDownloadBook}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg shadow hover:bg-amber-700 transition-colors text-sm font-bold"
                >
                    <Icons.Book className="w-4 h-4" /> Download Full Book
                </button>
            )}
        </div>
      </div>
      
      <div className={viewModeOutline === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "grid gap-4"}>
        {project.outline.map((chapter, index) => {
          const locked = isChapterLocked(index);
          const isEditing = editingChapterId === chapter.id;

          if (isEditing && tempChapterData) {
            return (
              <div key={chapter.id} className="p-4 border-2 border-indigo-100 bg-white dark:bg-gray-800 rounded-xl shadow-lg space-y-3">
                 <input 
                   className="w-full font-bold text-lg border-b border-gray-200 dark:border-gray-700 focus:border-indigo-500 outline-none pb-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                   value={tempChapterData.title}
                   onChange={e => setTempChapterData({...tempChapterData, title: e.target.value})}
                 />
                 <textarea 
                   className="w-full text-sm text-gray-900 dark:text-gray-100 border rounded p-2 focus:ring-2 focus:ring-indigo-100 outline-none min-h-[80px] bg-white dark:bg-gray-800 dark:border-gray-700 placeholder-gray-400"
                   value={tempChapterData.summary}
                   onChange={e => setTempChapterData({...tempChapterData, summary: e.target.value})}
                 />
                 <div className="flex justify-end gap-2">
                   <button onClick={(e) => { e.stopPropagation(); setEditingChapterId(null); }} className="text-xs px-3 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Cancel</button>
                   <button onClick={saveChapterEdit} className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">Save Changes</button>
                 </div>
              </div>
            );
          }

          return (
            <div 
              key={chapter.id} 
              className={`
                group relative p-5 border rounded-xl transition-all duration-200 flex flex-col
                ${locked 
                  ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 opacity-70 cursor-not-allowed' 
                  : 'cursor-pointer hover:shadow-md hover:border-amber-200 dark:hover:border-amber-900 bg-white dark:bg-gray-800'
                }
                ${chapter.id === activeChapterId && !locked ? 'ring-2 ring-amber-400 border-amber-400 bg-amber-50/50 dark:bg-amber-900/20' : ''}
              `}
              onClick={() => { 
                if (!locked) {
                  onStateChange({ activeChapterId: chapter.id, activeTab: 'outline' }); 
                  setSidebarOpen(false); 
                }
              }}
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <span className={`
                    flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shrink-0
                    ${locked ? 'bg-gray-200 dark:bg-gray-700 text-gray-500' : chapter.isDrafted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}
                  `}>
                    {chapter.number}
                  </span>
                  <h3 className={`font-bold text-lg leading-tight ${locked ? 'text-gray-400' : 'text-ink dark:text-gray-100'}`}>
                    {chapter.title}
                  </h3>
                </div>
                
                <div className="flex items-center gap-2 shrink-0">
                  {locked ? (
                    <Icons.Lock className="text-gray-300 w-5 h-5" />
                  ) : (
                    <>
                      {chapter.isDrafted && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">Ready</span>}
                      <button 
                        onClick={(e) => startEditChapter(e, chapter)}
                        className="p-1 text-gray-300 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Edit Summary"
                      >
                        <Icons.Pen className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <p className={`text-sm ${locked ? 'text-gray-400' : 'text-gray-600 dark:text-gray-400'} leading-relaxed ${viewModeOutline === 'list' ? 'pl-10' : 'mt-2'}`}>
                {locked ? "Finish the previous chapter to unlock this plot point." : chapter.summary}
              </p>
            </div>
          );
        })}
      </div>
    </div>
    );
  };

  const renderCharactersTab = () => (
    <div className="space-y-6 pb-20 animate-fade-in-up">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-serif font-bold text-ink dark:text-gray-100">Cast of Characters</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {project.characters.map(char => {
          const isEditing = editingCharId === char.id;
          
          if (isEditing && tempCharData) {
            return (
              <div key={char.id} className="p-5 bg-white dark:bg-gray-800 border-2 border-indigo-100 dark:border-gray-700 rounded-xl shadow-lg space-y-3">
                <div className="flex gap-2">
                  <input 
                    className="flex-1 font-bold text-lg border-b border-gray-200 dark:border-gray-700 focus:border-indigo-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    value={tempCharData.name}
                    onChange={e => setTempCharData({...tempCharData, name: e.target.value})}
                    placeholder="Name"
                  />
                  <input 
                    className="w-1/3 text-sm text-right border-b border-gray-200 dark:border-gray-700 focus:border-indigo-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    value={tempCharData.role}
                    onChange={e => setTempCharData({...tempCharData, role: e.target.value})}
                    placeholder="Role"
                  />
                </div>
                <div>
                   <label className="text-xs text-gray-400 uppercase font-bold">Description</label>
                   <textarea 
                      className="w-full text-sm border rounded p-2 mt-1 outline-none focus:ring-2 focus:ring-indigo-100 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                      rows={3}
                      value={tempCharData.description}
                      onChange={e => setTempCharData({...tempCharData, description: e.target.value})}
                   />
                </div>
                <div>
                   <label className="text-xs text-gray-400 uppercase font-bold">Arc</label>
                   <textarea 
                      className="w-full text-sm border rounded p-2 mt-1 outline-none focus:ring-2 focus:ring-indigo-100 bg-white dark:bg-gray-800 dark:border-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                      rows={2}
                      value={tempCharData.arc}
                      onChange={e => setTempCharData({...tempCharData, arc: e.target.value})}
                   />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                   <button onClick={() => setEditingCharId(null)} className="text-xs px-3 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Cancel</button>
                   <button onClick={saveCharEdit} className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>
                </div>
              </div>
            );
          }

          return (
            <div key={char.id} className="group relative p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <button 
                onClick={() => startEditChar(char)}
                className="absolute top-4 right-4 text-gray-300 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icons.Pen className="w-4 h-4" />
              </button>
              
              <div className="flex justify-between items-start mb-3 pr-6">
                <h3 className="font-bold text-lg text-ink dark:text-gray-100">{char.name}</h3>
                <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full font-medium">{char.role}</span>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">{char.description}</p>
              <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                <strong className="text-gray-700 dark:text-gray-300">Arc:</strong> {char.arc}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderWorldTab = () => (
    <div className="space-y-6 pb-20 animate-fade-in-up">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-serif font-bold text-ink dark:text-gray-100">World Bible</h2>
        <button 
           onClick={addWorldItem}
           className="flex items-center gap-2 px-4 py-2 bg-ink dark:bg-gray-700 text-white rounded-lg text-sm font-bold shadow hover:bg-opacity-90"
        >
            <Icons.Globe className="w-4 h-4" /> Add Item
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
         {(project.worldBible || []).length === 0 && (
            <div className="col-span-full py-12 text-center text-gray-400 italic">
               No world details added yet. Click "Add Item" to document locations, lore, or objects.
            </div>
         )}
         
         {(project.worldBible || []).map(item => {
             const isEditing = editingWorldId === item.id;
             if (isEditing && tempWorldData) {
                 return (
                    <div key={item.id} className="p-5 bg-white dark:bg-gray-800 border-2 border-indigo-100 dark:border-gray-700 rounded-xl shadow-lg space-y-3">
                        <div className="flex gap-2">
                           <input 
                              className="flex-1 font-bold text-lg border-b bg-transparent outline-none focus:border-indigo-500"
                              value={tempWorldData.name}
                              onChange={e => setTempWorldData({...tempWorldData, name: e.target.value})}
                              placeholder="Name"
                           />
                           <select 
                             className="text-sm border rounded bg-transparent outline-none"
                             value={tempWorldData.category}
                             // @ts-ignore
                             onChange={e => setTempWorldData({...tempWorldData, category: e.target.value})}
                           >
                              {['Location', 'Lore', 'Object', 'Faction', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                           </select>
                        </div>
                        <textarea 
                           className="w-full text-sm border rounded p-2 mt-1 bg-transparent outline-none focus:ring-2 focus:ring-indigo-100 h-24"
                           value={tempWorldData.description}
                           onChange={e => setTempWorldData({...tempWorldData, description: e.target.value})}
                           placeholder="Description"
                        />
                        <div className="flex justify-between pt-2">
                           <button onClick={() => deleteWorldItem(item.id)} className="text-xs text-red-500 hover:text-red-700">Delete</button>
                           <div className="flex gap-2">
                              <button onClick={() => setEditingWorldId(null)} className="text-xs px-3 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">Cancel</button>
                              <button onClick={saveWorldEdit} className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>
                           </div>
                        </div>
                    </div>
                 );
             }

             return (
                <div key={item.id} className="group relative p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <button onClick={() => startEditWorld(item)} className="absolute top-4 right-4 text-gray-300 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"><Icons.Pen className="w-4 h-4" /></button>
                    <div className="flex items-center gap-2 mb-2">
                       <h3 className="font-bold text-lg text-ink dark:text-gray-100">{item.name}</h3>
                       <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">{item.category}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                </div>
             );
         })}
      </div>
    </div>
  );

  const renderWriteView = () => {
    if (!activeChapter) return null;

    if (!activeChapter.isDrafted) {
      return (
        <div className="h-full flex flex-col items-center justify-center animate-fade-in p-6 relative">
            <button 
                onClick={() => onStateChange({ activeChapterId: null })}
                className="absolute top-0 left-0 flex items-center gap-2 text-gray-500 hover:text-indigo-600 font-medium text-sm transition-colors"
            >
                <Icons.ArrowLeft className="w-4 h-4" /> Back to Outline
            </button>

            <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-gray-700 text-center">
                <div className="text-sm font-bold text-amber-600 uppercase tracking-wider mb-2">
                    Chapter {activeChapter.number}
                </div>
                <h2 className="text-3xl font-serif font-bold text-ink dark:text-gray-100 mb-6">
                    {activeChapter.title}
                </h2>
                
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 mb-8 text-left">
                    <h4 className="text-xs font-bold text-gray-500 uppercase mb-2">Chapter Plan</h4>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed italic">
                        {activeChapter.summary}
                    </p>
                </div>

                <div className="flex flex-col gap-4 items-center">
                    <button 
                        onClick={handleWriteChapter}
                        disabled={isWriting}
                        className="w-full sm:w-auto px-8 py-4 bg-ink dark:bg-black text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isWriting ? (
                            <>Writing... <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /></>
                        ) : (
                            <>Generate Chapter with AI <Icons.Sparkles className="w-5 h-5" /></>
                        )}
                    </button>
                    
                    <button 
                       onClick={handleStartManual}
                       className="text-sm text-gray-400 hover:text-gray-600 underline"
                    >
                        Or start with a blank page
                    </button>
                </div>
            </div>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col animate-fade-in relative">
        {/* Editor Toolbar - Only visible when NOT in Zen Mode */}
        {!isZenMode && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2 flex-shrink-0 transition-all">
            <div className="flex items-center gap-4">
               {/* Enhanced Back Button */}
               <button 
                 onClick={() => onStateChange({ activeChapterId: null })}
                 className="group flex items-center gap-2 px-3 py-2 text-gray-500 hover:text-ink dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                 title="Back to Outline"
               >
                 <Icons.ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                 <span className="text-sm font-bold">Outline</span>
               </button>
               <div>
                  <div className="flex items-center gap-2 mb-1">
                     <div className="text-xs text-amber-600 font-bold uppercase tracking-wider">Chapter {activeChapter.number}</div>
                     <span className="text-xs text-gray-300">|</span>
                     <span className="text-xs text-gray-400">Autosaved {lastSaved.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                  </div>
                  <h2 className="text-2xl font-serif font-bold text-ink dark:text-gray-100">{activeChapter.title}</h2>
               </div>
            </div>
            
            <div className="flex gap-2">
               <button 
                 onClick={() => setIsZenMode(true)}
                 className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-500 hover:text-ink hover:bg-gray-50 dark:hover:bg-gray-700"
                 title="Zen Mode"
               >
                 <Icons.Maximize className="w-4 h-4" />
               </button>
               <button 
                 onClick={handleCritique}
                 className={`flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 ${showCritique ? 'bg-red-50 text-red-600 border-red-200' : 'bg-white dark:bg-gray-800 text-gray-500 hover:text-ink'}`}
                 title="Red Pen Critique"
               >
                 <Icons.Feather className="w-4 h-4" />
               </button>
               <button 
                 onClick={toggleTTS}
                 className={`flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 dark:border-gray-600 ${isSpeaking ? 'bg-amber-100 text-amber-600 animate-pulse' : 'bg-white dark:bg-gray-800 text-gray-500 hover:text-ink'}`}
                 title="Read Aloud"
               >
                 {isSpeaking ? <Icons.Pause className="w-4 h-4" /> : <Icons.Play className="w-4 h-4" />}
               </button>
              <button 
                onClick={() => setShowFindReplace(!showFindReplace)}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-ink"
                title="Find & Replace"
              >
                 <Icons.Search className="w-4 h-4" />
              </button>
              <button 
                onClick={handleDownloadChapter}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-ink"
                title="Download Chapter"
              >
                 <Icons.Save className="w-4 h-4" />
              </button>
              <button 
                onClick={handleWriteChapter}
                disabled={isWriting}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-sm font-medium shadow-sm hover:shadow-md"
              >
                {isWriting ? (
                  <>Writing <div className="w-4 h-4 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" /></>
                ) : (
                  <>Regenerate <Icons.Sparkles className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        )}
        
        {/* Find & Replace Bar */}
        {showFindReplace && !isZenMode && (
          <div className="mb-4 bg-gray-100 dark:bg-gray-800 p-2 rounded-lg flex items-center gap-2 animate-slide-down">
             <input value={findText} onChange={e => setFindText(e.target.value)} placeholder="Find..." className="flex-1 p-1 px-3 rounded text-sm outline-none border border-transparent focus:border-indigo-500 text-black" />
             <input value={replaceText} onChange={e => setReplaceText(e.target.value)} placeholder="Replace with..." className="flex-1 p-1 px-3 rounded text-sm outline-none border border-transparent focus:border-indigo-500 text-black" />
             <button onClick={handleGlobalReplace} className="px-3 py-1 bg-indigo-600 text-white rounded text-sm font-bold">Replace All</button>
             <button onClick={() => setShowFindReplace(false)} className="px-2 text-gray-400 hover:text-gray-600"><Icons.X className="w-4 h-4" /></button>
             {replaceResult && <span className="text-xs font-medium text-green-600 ml-2">{replaceResult}</span>}
          </div>
        )}

        {/* Editor Container */}
        <div className={`flex-1 min-h-0 relative rounded-lg shadow-sm bg-white dark:bg-gray-900 overflow-hidden transition-all ${isZenMode ? 'fixed inset-0 z-50 rounded-none' : 'border border-gray-200 dark:border-gray-700'}`}>
          <Editor 
            key={activeChapter.id}
            content={activeChapter.content || ''} 
            onUpdate={handleUpdateContent}
            zenMode={isZenMode}
          />
          
          {/* Zen Mode Exit Button */}
          {isZenMode && (
             <button 
               onClick={() => setIsZenMode(false)}
               className="absolute top-6 right-6 p-2 bg-gray-100/50 hover:bg-gray-200 dark:bg-gray-800/50 dark:hover:bg-gray-700 rounded-full text-gray-500 transition-colors"
               title="Exit Zen Mode"
             >
               <Icons.Minimize className="w-5 h-5" />
             </button>
          )}
          
          {/* Aesthetic Bottom Actions Overlay */}
          {!isZenMode && (
             <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 animate-slide-up">
               <div className="flex items-center p-1.5 gap-1 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 rounded-full shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
                  
                  <button
                     onClick={handleContinueWriting}
                     disabled={isContinuing}
                     className="group relative flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-[length:200%_auto] hover:bg-right transition-all duration-500 text-white dark:text-black font-bold text-sm shadow-lg shadow-black/10 hover:shadow-black/20 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:hover:shadow-none"
                  >
                     {isContinuing ? (
                       <div className="w-4 h-4 border-2 border-white/50 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                     ) : (
                       <Icons.Sparkles className="w-4 h-4 group-hover:text-yellow-300 dark:group-hover:text-yellow-600 transition-colors" />
                     )}
                     <span>Continue Writing</span>
                  </button>

                  <div className="w-px h-6 bg-gray-300/50 dark:bg-gray-600/50 mx-1"></div>

                  <button
                     onClick={handleNextChapter}
                     className="flex items-center gap-2 px-5 py-3 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 font-bold text-sm transition-all hover:text-ink dark:hover:text-white group"
                  >
                     <span>Next Chapter</span>
                     <Icons.ArrowLeft className="w-4 h-4 rotate-180 group-hover:translate-x-1 transition-transform" />
                  </button>
               </div>
             </div>
          )}
        </div>
      </div>
    );
  };

  const renderSettingsTab = () => (
    <div className="space-y-6 animate-fade-in-up">
      <h2 className="text-2xl font-serif font-bold text-ink dark:text-gray-100">Project Settings</h2>
      <div className="bg-white dark:bg-gray-800 p-8 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm max-w-2xl">
        <h3 className="font-bold text-lg mb-6 border-b dark:border-gray-700 pb-2 text-ink dark:text-gray-100">Book Details</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Title</label>
            <input 
              className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
              value={project.title}
              onChange={(e) => onStateChange({ project: {...project, title: e.target.value }})}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
             <div>
               <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Genre</label>
               <input 
                 className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                 value={project.genre}
                 onChange={(e) => onStateChange({ project: {...project, genre: e.target.value }})}
               />
             </div>
             <div>
               <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Style</label>
               <input 
                 className="w-full p-2 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                 value={project.style}
                 onChange={(e) => onStateChange({ project: {...project, style: e.target.value }})}
               />
             </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700 space-y-4">
           {isBookComplete && (
              <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-100 dark:border-green-800 flex justify-between items-center">
                 <div>
                   <h4 className="font-bold text-green-900 dark:text-green-100 text-sm">Book Completed!</h4>
                   <p className="text-xs text-green-700 dark:text-green-300 mt-1">All chapters are drafted.</p>
                 </div>
                 <button onClick={handleDownloadBook} className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded shadow hover:bg-green-700 transition-colors">
                   Download Full Book
                 </button>
               </div>
           )}

           <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-lg border border-red-100 dark:border-red-800 flex justify-between items-center">
             <div>
               <h4 className="font-bold text-red-900 dark:text-red-100 text-sm">Logout</h4>
               <p className="text-xs text-red-700 dark:text-red-300 mt-1">End session and return to home.</p>
             </div>
             <button onClick={onLogout} type="button" className="px-4 py-2 bg-white dark:bg-gray-700 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-300 text-sm font-medium rounded hover:bg-red-50 dark:hover:bg-red-900/50 transition-colors">
               Logout
             </button>
           </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'outline': return renderOutlineTab();
      case 'characters': return renderCharactersTab();
      case 'world': return renderWorldTab();
      // 'write' tab removed from menu but logic preserved via outline selection
      case 'settings': return renderSettingsTab();
      default: return null;
    }
  };

  return (
    <div className={`h-screen flex flex-col md:flex-row overflow-hidden transition-colors duration-300 ${state.themeMode === 'dark' ? 'dark bg-gray-900 text-gray-100' : state.themeMode === 'sepia' ? 'bg-[#f4e4bc] text-gray-900' : 'bg-paper text-gray-900'}`}>
      
      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm transition-opacity" onClick={() => setSidebarOpen(false)}></div>
      )}

      {/* Main Sidebar (Hidden in Zen Mode) */}
      <div className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 shadow-xl md:shadow-none
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isZenMode ? 'hidden md:hidden' : ''}
      `}>
        <div className="p-6 border-b border-gray-100 dark:border-gray-800 hidden md:block">
           <div className="flex items-center justify-between mb-2">
              <button 
                onClick={() => onStateChange({ viewMode: 'projects' })}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-indigo-600 transition-colors"
              >
                <Icons.ArrowLeft className="w-3 h-3" /> All Projects
              </button>
           </div>
           <h1 className="font-serif font-bold text-lg text-ink dark:text-white truncate leading-tight">{project.title}</h1>
           <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span> Auto-save on
           </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button 
            onClick={() => { onStateChange({ activeTab: 'outline' }); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === 'outline' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 font-bold shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-ink dark:hover:text-white'}`}
          >
            <Icons.Book /> <span>Outline</span>
          </button>
          <button 
            onClick={() => { onStateChange({ activeTab: 'characters' }); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === 'characters' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 font-bold shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-ink dark:hover:text-white'}`}
          >
            <Icons.Users /> <span>Characters</span>
          </button>
          <button 
            onClick={() => { onStateChange({ activeTab: 'world' }); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === 'world' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 font-bold shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-ink dark:hover:text-white'}`}
          >
            <Icons.Globe /> <span>World Bible</span>
          </button>
          <button 
            onClick={() => { onStateChange({ activeTab: 'settings' }); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === 'settings' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100 font-bold shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-ink dark:hover:text-white'}`}
          >
            <Icons.Settings /> <span>Settings</span>
          </button>
        </nav>
        
        {/* Footer Actions */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800">
           <button 
              onClick={() => onStateChange({ viewMode: 'projects' })}
              className="w-full flex items-center justify-center gap-2 mb-3 text-sm font-medium text-gray-500 hover:text-ink dark:hover:text-white transition-colors"
           >
              <Icons.ArrowLeft className="w-3 h-3" /> Back to Projects
           </button>
           
           <div className="flex justify-center gap-2">
             <button onClick={() => onStateChange({ themeMode: 'light' })} className={`p-2 rounded-lg ${state.themeMode === 'light' ? 'bg-gray-200' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`} title="Light Mode"><Icons.Sun className="text-gray-600 dark:text-gray-400" /></button>
             <button onClick={() => onStateChange({ themeMode: 'sepia' })} className={`p-2 rounded-lg ${state.themeMode === 'sepia' ? 'bg-[#e3d0a4]' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`} title="Sepia Mode"><Icons.Coffee className="text-amber-800 dark:text-gray-400" /></button>
             <button onClick={() => onStateChange({ themeMode: 'dark' })} className={`p-2 rounded-lg ${state.themeMode === 'dark' ? 'bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`} title="Dark Mode"><Icons.Moon className="text-gray-600 dark:text-gray-400" /></button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <main className={`flex-1 flex flex-col h-full w-full relative selection:bg-amber-100 dark:selection:bg-indigo-900 ${isZenMode ? '' : 'overflow-hidden'}`}>
        
        {/* Unified Dashboard Header - Hidden in Zen Mode */}
        {!isZenMode && (
          <header className="h-16 flex items-center justify-between px-6 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 z-20 flex-shrink-0">
             <div className="flex items-center gap-4">
               {/* Mobile Menu Toggle */}
               <button 
                 onClick={() => setSidebarOpen(!sidebarOpen)} 
                 className="md:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
               >
                 <Icons.Menu className="w-5 h-5" />
               </button>
               
               {/* Mobile Title (only visible on mobile to save space) */}
               <h1 className="md:hidden font-serif font-bold text-lg text-ink dark:text-white truncate max-w-[150px]">{project.title}</h1>
             </div>

             <div className="flex items-center gap-3 ml-auto">
               <button 
                 onClick={() => setShowMuse(!showMuse)}
                 className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-sm border border-gray-200 dark:border-gray-600 transition-all ${showMuse ? 'bg-indigo-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
               >
                 <Icons.Sparkles className="w-4 h-4" /> 
                 <span className="text-sm font-bold hidden sm:inline">Ask Muse</span>
               </button>
             </div>
          </header>
        )}

        <div className={`flex-1 overflow-y-auto p-4 md:p-8 ${isZenMode ? 'p-0 md:p-0' : ''}`}>
           <div className={`mx-auto h-full flex flex-col ${isZenMode ? 'max-w-full' : 'max-w-5xl'}`}>
             {renderTabContent()}
           </div>
        </div>
      </main>

      {/* Muse Sidebar (Updated) */}
      {showMuse && !isZenMode && !showCritique && (
        <div className="fixed inset-y-0 right-0 z-[60] w-[400px] bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-left transition-transform">
           {/* Muse Header */}
           <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-white dark:from-gray-800 dark:to-gray-900">
              <div className="flex items-center gap-3">
                 <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white shadow-md relative">
                   <Icons.Sparkles className="w-5 h-5" />
                   {/* Online dot */}
                   <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white dark:border-gray-800 rounded-full"></div>
                 </div>
                 <div>
                    <h3 className="font-bold text-ink dark:text-white leading-tight">The Muse</h3>
                    <p className="text-[10px] uppercase tracking-wider text-indigo-500 font-bold">
                       {museContext ? `Context: ${museContext.toUpperCase()}` : 'AI Agent'}
                    </p>
                 </div>
              </div>
              <div className="flex items-center gap-1">
                 <button onClick={handleNewChat} className="p-2 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-indigo-50 dark:hover:bg-gray-800 transition-colors" title="New Chat">
                   <Icons.Refresh className="w-4 h-4" />
                 </button>
                 <button onClick={() => setShowMuse(false)} className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                   <Icons.X className="w-5 h-5" />
                 </button>
              </div>
           </div>

           {/* Context Selector (If no context selected) */}
           {!museContext ? (
               <div className="flex-1 overflow-y-auto p-6 flex flex-col items-center justify-center bg-gray-50 dark:bg-black/20">
                   <div className="text-center mb-8">
                       <h4 className="text-xl font-serif font-bold text-gray-800 dark:text-gray-200 mb-2">How can I help you today?</h4>
                       <p className="text-sm text-gray-500">Select a context to give the AI agent focus.</p>
                   </div>
                   
                   <div className="grid grid-cols-2 gap-3 w-full">
                       <button onClick={() => startMuseSession('story')} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl shadow-sm hover:shadow-md transition-all text-left group">
                           <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform"><Icons.Book /></div>
                           <span className="font-bold text-gray-700 dark:text-gray-200 block text-sm">Full Story</span>
                           <span className="text-xs text-gray-400">Plot, themes, tone</span>
                       </button>
                       <button onClick={() => startMuseSession('chapter')} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl shadow-sm hover:shadow-md transition-all text-left group">
                           <div className="w-8 h-8 rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform"><Icons.Pen /></div>
                           <span className="font-bold text-gray-700 dark:text-gray-200 block text-sm">Current Chapter</span>
                           <span className="text-xs text-gray-400">Rewrite, expand, edit</span>
                       </button>
                       <button onClick={() => startMuseSession('characters')} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl shadow-sm hover:shadow-md transition-all text-left group">
                           <div className="w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform"><Icons.Users /></div>
                           <span className="font-bold text-gray-700 dark:text-gray-200 block text-sm">Characters</span>
                           <span className="text-xs text-gray-400">Develop, add, refine</span>
                       </button>
                       <button onClick={() => startMuseSession('world')} className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-indigo-400 dark:hover:border-indigo-500 rounded-xl shadow-sm hover:shadow-md transition-all text-left group">
                           <div className="w-8 h-8 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-600 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform"><Icons.Globe /></div>
                           <span className="font-bold text-gray-700 dark:text-gray-200 block text-sm">World Bible</span>
                           <span className="text-xs text-gray-400">Lore, locations, items</span>
                       </button>
                   </div>
                   <button onClick={() => startMuseSession('brainstorm')} className="mt-4 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 underline">Just general brainstorming...</button>
               </div>
           ) : (
             /* Chat Area */
             <>
               <div className="flex-1 overflow-y-auto p-5 space-y-6">
                  {(project.museHistory || []).length === 0 && (
                     <div className="text-center p-8 text-gray-400">
                        <p className="text-sm">I'm ready. What's on your mind?</p>
                     </div>
                  )}
                  
                  {(project.museHistory || []).map((msg) => (
                     <div key={msg.id} className={`flex w-full flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                        <div className={`max-w-[90%] p-4 rounded-2xl shadow-sm text-sm leading-relaxed ${
                           msg.role === 'user' 
                           ? 'bg-indigo-600 text-white rounded-br-none' 
                           : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200 rounded-bl-none'
                        }`}>
                           {msg.text}
                        </div>
                        
                        {/* Agent Action Proposal Card */}
                        {msg.actionProposal && (
                            <div className="mt-2 w-[90%] bg-gradient-to-br from-indigo-50 to-white dark:from-gray-800 dark:to-gray-900 border border-indigo-100 dark:border-gray-700 rounded-xl p-4 shadow-md animate-fade-in-up">
                                <div className="flex items-start gap-3 mb-2">
                                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg text-indigo-600">
                                        {msg.actionProposal.type === 'create_character' ? <Icons.Users className="w-5 h-5" /> : <Icons.Pen className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 dark:text-gray-200 text-sm">{msg.actionProposal.label}</h4>
                                        <p className="text-xs text-gray-500">{msg.actionProposal.description}</p>
                                    </div>
                                </div>
                                <div className="bg-white/50 dark:bg-black/20 rounded p-2 text-xs text-gray-600 dark:text-gray-400 font-mono mb-3 max-h-32 overflow-y-auto border border-gray-100 dark:border-gray-700">
                                    {msg.actionProposal.type === 'rewrite_chapter' ? (
                                        <div className="italic">"{msg.actionProposal.data.content.slice(0, 150)}..."</div>
                                    ) : (
                                        <pre>{JSON.stringify(msg.actionProposal.data, null, 2)}</pre>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => handleApplyAction(msg.actionProposal!, msg.id)}
                                        className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 shadow-sm transition-colors"
                                    >
                                        Apply Changes
                                    </button>
                                </div>
                            </div>
                        )}
                     </div>
                  ))}
    
                  {isMuseThinking && (
                     <div className="flex justify-start w-full">
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl rounded-bl-none border border-gray-100 dark:border-gray-700 shadow-sm flex items-center gap-2">
                           <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                           <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                           <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                        </div>
                     </div>
                  )}
                  <div ref={chatEndRef} />
               </div>
    
               {/* Input Area */}
               <form onSubmit={handleMuseSubmit} className="p-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur border-t border-gray-200 dark:border-gray-800">
                  <div className="relative flex items-center">
                     <input 
                        className="w-full pl-4 pr-12 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm dark:text-white text-sm"
                        placeholder="Ask or type 'Skip'..."
                        value={museInput}
                        onChange={(e) => setMuseInput(e.target.value)}
                        disabled={isMuseThinking}
                     />
                     <button 
                       type="submit" 
                       disabled={!museInput || isMuseThinking} 
                       className="absolute right-2 p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors shadow-md"
                     >
                        <Icons.ChevronUp className="w-5 h-5" />
                     </button>
                  </div>
                  <div className="text-center mt-2">
                     <button type="button" onClick={() => setMuseInput("Skip")} className="text-[10px] text-gray-400 hover:text-indigo-500 uppercase font-bold tracking-wider">
                        Skip Question / Surprise Me
                     </button>
                  </div>
               </form>
             </>
           )}
        </div>
      )}

      {/* Critique Sidebar (Red Pen) */}
      {showCritique && !isZenMode && (
         <div className="fixed inset-y-0 right-0 z-[60] w-96 bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col animate-slide-left overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-bold">
                 <Icons.Feather /> The Red Pen
              </div>
              <button onClick={() => setShowCritique(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400"><Icons.X /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-black/20">
               {critiqueLoading && (
                  <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
                     <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
                     <p className="text-gray-500 text-sm animate-pulse">Analyzing pacing, tone, and dialogue...</p>
                  </div>
               )}

               {!critiqueLoading && critiqueData.length === 0 && (
                  <div className="text-center text-gray-500 mt-10">
                     <p>No issues found (or click the feather to scan).</p>
                  </div>
               )}

               {critiqueData.map((item, idx) => (
                  <div key={idx} className="bg-white dark:bg-gray-800 p-4 rounded-xl border-l-4 border-red-500 shadow-sm">
                     <div className="flex justify-between items-start mb-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-red-500 bg-red-50 dark:bg-red-900/30 px-2 py-1 rounded">
                           {item.type}
                        </span>
                     </div>
                     <blockquote className="text-xs italic text-gray-500 dark:text-gray-400 border-l-2 border-gray-200 pl-2 mb-3">
                        "{item.quote}"
                     </blockquote>
                     <p className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">{item.feedback}</p>
                     <p className="text-sm text-gray-600 dark:text-gray-400 bg-green-50 dark:bg-green-900/20 p-2 rounded text-green-800 dark:text-green-300">
                        <strong>Fix:</strong> {item.suggestion}
                     </p>
                  </div>
               ))}
            </div>
         </div>
      )}
      {notification && <NotificationToast message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
    </div>
  );
};